import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Form, Input, Select, Spin } from "antd";
import { fetchData, save } from "../../features/auth/authSlice";
import { useEffect, useRef, useState } from "react";
import Header from "./Header";
import { cleanOrgObj } from "../../features/obj/objSlice";
import toast from "react-hot-toast";
import RightSide from "./RightSide"; 
import PhoneInput from "react-phone-input-2";
import { fetchattachmentRequirements } from "../../features/fetch/fetchSlice";
import checkBox from "../../assets/svg/Checkbox.svg";
import ReactPasswordChecklist from "react-password-checklist";
import { CheckCircleOutlined } from "@ant-design/icons";
import { isValidPhoneNumber } from "libphonenumber-js";

export default function Register() {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { authLoading, saving } = useSelector((state) => state.auth);
  const { attRequirements } = useSelector((state) => state.fetch);
  const { subdomain } = useSelector((state) => state.global);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { orgObj } = useSelector((state) => state.obj);

  const urlCat = "api/v1/public/industries";

  const { loading, data } = useSelector((state) => ({
    loading: state.auth.loading,
    data: state.auth.data[urlCat] || [],
  }));

  const formRef = useRef(null);

  const onFinish = async (data) => {
    const isPasswordValid =
      password.length >= 8 &&
      /\d/.test(password) &&
      /[A-Z]/.test(password) &&
      /[!@#$%^&*()_+[\]{};':"\\|,.<>/?]+/.test(password) &&
      password === confirmPassword;
    if (!isPasswordValid) {
      toast.error(
        "Password does not meet the required conditions or does not match"
      );
      return;
    }
    data.usrEncryptedPassword = password;

    const res = await dispatch(
      save({
        ...data,
        url: "api/v1/public/create-organisation",
      })
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.messages?.message);
      await dispatch(cleanOrgObj());
      await form.resetFields();
      await navigate("/registration-success");
    } else {
      toast.error(res?.payload?.messages?.message);
    }
  };

  const handleSignin = () => {
    navigate("/login");
  };

  async function fetchattachmentRequirementsData() {
    await dispatch(
      fetchattachmentRequirements({
        url: `api/v1/onboard/get-attachment-requirements`,
        attrCat: "BUSINESS_TYPES",
      })
    );
  }

  useEffect(() => {
    fetchattachmentRequirementsData();
    dispatch(fetchData({
        url: urlCat,
      })
    );
  }, []);
 
  return (
    <div className="overflow-y-auto min-h-screen h-screen w-full">
      <div className="flex  w-full  min-h-screen bg-white">
        <div className="flex flex-col w-full ">
          <Header />
          <div className="w-full h-full flex flex-col justify-center items-center">
            <div className="w-full  flex flex-col lg:px-[6.25rem] px-3">
              <span
                className={`heading_1 ${
                  subdomain === "ndovupay" || subdomain === "localhost"
                    ? "!text-[#DB2A12]"
                    : "text-inherit"
                }`}
              >
                Register
              </span>
              <span className="paragraph_1 mt-[1.13rem]">
                Fill in the details below to create an account
              </span>

              <Form
                layout="vertical"
                ref={formRef}
                name="registration"
                onFinish={onFinish}
                scrollToFirstError
                form={form}
                initialValues={orgObj}
                style={{
                  maxWidth: "100%",
                  marginTop: "2.06rem",
                  width: "100%",
                }}
              >
                <Form.Item
                  label="Organization Name"
                  name="orgName"
                  rules={[
                    {
                      required: true,
                      message: "Organization name is required",
                    },
                  ]}
                >
                  <Input className="input" />
                </Form.Item>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-[1.25rem]">
                  <Form.Item
                    name="orgEmail"
                    label="Organization Email"
                    rules={[
                      {
                        required: true,
                        message: "Organization Email is required to login",
                      },
                      {
                        type: "email",
                        message:
                          "Please enter a valid organization email address",
                      },
                    ]}
                  >
                    <Input type="email" className="input" />
                  </Form.Item>

                  <Form.Item
                    label="Organization Phone"
                    name="orgMobile"
                    validateFirst
                    rules={[
                      {
                        required: true,
                        message: "Phone number is required",
                      },
                      {
                        validator: (_, value) => {
                          if (!value) {
                            return Promise.reject(
                              new Error("Phone number is required")
                            );
                          } 
                          const phoneWithPlus = value.startsWith('+') ? value : `+${value}`; 
                          if (isValidPhoneNumber(phoneWithPlus)) {
                            return Promise.resolve();
                          }

                          return Promise.reject(
                            new Error(
                              "Please enter a valid phone number for the selected country"
                            )
                          );
                        },
                      },
                    ]}
                  >
                    <PhoneInput
                      enableSearch
                      country="ke"
                      countryCodeEditable={false}
                      className="input"
                    />
                  </Form.Item>

                  <Form.Item
                    name="orgBizTypeId"
                    className=""
                    label={"Business Type"}
                    rules={[
                      {
                        required: true,
                        message: "Please select Business Types",
                      },
                    ]}
                  >
                    <Select
                      className=""
                      allowClear
                      style={{
                        width: "100%",
                      }}
                      options={
                        attRequirements?.length > 0 &&
                        attRequirements?.map((item) => ({
                          value: item?.attrId,
                          label: item?.attrName,
                        }))
                      }
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label?.toLocaleLowerCase() ?? "").includes(
                          input?.toLocaleLowerCase()
                        )
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    name="businessIndustryId"
                    className=""
                    label={"Industry"}
                    rules={[
                      {
                        required: false,
                        message: "Please select industry",
                      },
                    ]}
                  >
                    <Select
                      className=""
                      allowClear
                      style={{
                        width: "100%",
                      }}
                      options={
                        data?.length > 0 &&
                        data?.map((item) => ({
                          value: item?.id,
                          label: item?.name,
                        }))
                      }
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label?.toLocaleLowerCase() ?? "").includes(
                          input?.toLocaleLowerCase()
                        )
                      }
                    />
                  </Form.Item>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-[1.25rem]">
                  <Form.Item
                    name="password"
                    label="Password"
                    rules={[
                      {
                        required: true,
                        message: "Please add password",
                      },
                    ]}
                  >
                    <Input.Password
                      className="input"
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </Form.Item>

                  <Form.Item
                    name="confirmPassword"
                    label="Confirm Password"
                    rules={[
                      {
                        required: true,
                        message: "Please add confirm password",
                      },
                    ]}
                  >
                    <Input.Password
                      className="input"
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </Form.Item>
                   
                  <ReactPasswordChecklist
                    className="text-darkBlue"
                    rules={[
                      "minLength",
                      "specialChar",
                      "number",
                      "capital",
                      "match",
                    ]}
                    minLength={8}
                    value={password}
                    valueAgain={confirmPassword}
                    messages={{
                      minLength: "A minimum of 8 characters",
                      specialChar: "At least one symbol (~!@$&)",
                      number: "At least one number (0-9)",
                      capital: "At least one upper case letter (A-Z)",
                      match: "Confirm password to match with the password",
                    }}
                    iconComponents={{
                      ValidIcon: (
                        <CheckCircleOutlined className="text-[1rem] mr-[.5rem] text-[green]" />
                      ),
                      InvalidIcon: (
                        <img
                          className="mr-[.5rem]"
                          src={checkBox}
                          alt="checkBox"
                        />
                      ),
                    }}
                    validColor="#147CBC"
                    iconSize={2}
                  />
                </div>

                <div className="w-full flex flex-col  ">
                  <div className="flex   mt-[1.63rem]">
                    <button
                      className="cstm-btn-2 !bg-[#000000]"
                      type="submit"
                      disabled={saving}
                    >
                      {saving ? <Spin /> : "Register"}
                    </button>
                  </div>

                  <div className="w-full justify-center flex mt-[1.19rem]">
                    <span className="label_2 !text-[#0A0A0A]">
                      Already have an account ?
                    </span>
                    <button
                      type="button"
                      className=" label_2 !text-ndovuRed ml-[.1rem]"
                      onClick={handleSignin}
                    >
                      Login
                    </button>
                  </div>
                </div>
              </Form>
            </div>
          </div>
        </div>

        <RightSide />
      </div>
    </div>
  );
}
